// Renders a rendered lease/agreement string as a styled legal document —
// AJM letterhead, serif type, centered title, numbered headings, justified
// clauses — matching the PDF a pilot would otherwise receive. Used in the
// in-app signing step and the /lease-preview page.

const RE_SECTION = /^\d{1,2}\.\s+\S/; // "1. LEASE OF AIRCRAFT"
const RE_SUBSEC = /^\d{1,2}\.\d{1,2}\b/; // "1.1  Lease. ..."
const RE_SUBITEM = /^\([a-z0-9]+\)/; // "(a) ..."
const DIVIDERS = new Set(['RECITALS', 'ACKNOWLEDGMENT AND SIGNATURE']);

export function LeaseDocument({ text, className = '' }: { text: string; className?: string }) {
  const lines = text.split('\n');
  const title = (lines[0] ?? '').trim();
  const subtitle = (lines[1] ?? '').trim();
  const body = lines.slice(2);

  return (
    <div className={`overflow-y-auto rounded-lg border border-slate-200 bg-white ${className}`}>
      {/* Letterhead */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-navy-900/15 bg-white px-6 py-3">
        <img
          src={`${import.meta.env.BASE_URL}ajm-logo.png`}
          alt="AJM Aviation"
          className="h-6 w-auto select-none"
          draggable={false}
        />
      </div>

      <div className="px-6 py-6 font-serif text-[12.5px] leading-relaxed text-navy-900 sm:px-8">
        <h2 className="text-center text-lg font-bold tracking-tight text-navy-900">{title}</h2>
        {subtitle && <p className="mt-1 text-center text-sm text-ink-muted">{subtitle}</p>}
        <div className="mx-auto mb-5 mt-3 h-px w-16 bg-navy-900/30" />

        {body.map((raw, i) => {
          const s = raw.trim();
          if (!s) return <div key={i} className="h-2.5" />;
          if (DIVIDERS.has(s) || RE_SECTION.test(s))
            return (
              <p key={i} className="mt-4 font-bold text-navy-900">
                {s}
              </p>
            );
          if (RE_SUBITEM.test(s))
            return (
              <p key={i} className="mt-1 pl-6 text-justify">
                {s}
              </p>
            );
          // subsections and ordinary paragraphs
          return (
            <p key={i} className={`text-justify ${RE_SUBSEC.test(s) ? 'mt-2' : 'mt-1.5'}`}>
              {s}
            </p>
          );
        })}
      </div>
    </div>
  );
}
